import { StreamProcessorName, StreamProcessorSrc } from "../../worklets/stream-processor";
import type { Snapshot, Subscriber, Store } from "../../types";
import defaultState from "../state";

type CreateOptions = {
    src: string;
    autoplay?: boolean;
    loop?: boolean;
    initialVolume?: number;
    initialMute?: boolean;
    onload?: () => void;
    onplay?: () => void;
    onend?: () => void;
    onpause?: () => void;
    onstop?: () => void;
};

/**
 * PCM Player implementation using AudioWorklet
 * Provides a similar interface to Howl for playing PCM audio data
 */
export class PcmStreamStore implements Store {
    public src: string | null;
    private audioContext: AudioContext | null;
    private workletNode: AudioWorkletNode | null;
    private gainNode: GainNode | null;
    private subscriptions: Set<Subscriber>;
    private snapshot: Snapshot;
    private position: number;
    private duration: number;
    private sampleRate: number;
    private currentTime: number;
    private reader: ReadableStreamDefaultReader<Uint8Array<ArrayBufferLike>> | null = null;
    private abortController: AbortController | null = null;

    /**
     * Merges changes to the Snapshot with the instance variable and invokes all subscriber callbacks
     */
    private updateSnapshot(update: Partial<Snapshot>) {
        this.snapshot = {
            ...this.snapshot,
            ...update
        };

        this.subscriptions.forEach((cb) => cb());
    }

    constructor(options?: CreateOptions) {
        this.src = null;
        this.audioContext = null;
        this.workletNode = null;
        this.gainNode = null;
        this.sampleRate = 24000; // Default sample rate, will be updated from WAV header
        this.position = 0;
        this.duration = 0;
        this.currentTime = 0;
        this.subscriptions = new Set();
        this.snapshot = defaultState;
        this.reader = null;

        if (options !== undefined) {
            this.initPlayer(options);
        }
    }

    /**
     * Initialize the PCM player with the provided options
     * @param options The options for creating the PCM player
     */
    private async initPlayer(options: CreateOptions) {
        this.src = options.src;
        this.abortController = new AbortController();
        
        // Update state to loading
        this.updateSnapshot({
            isUnloaded: false,
            isLoading: false,
            isReady: false,
            error: undefined
        });

        try {
            // Fetch the audio data
            const response = await fetch(options.src, {
                signal: this.abortController.signal
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            // Check if we can get a ReadableStream from the response body
            if (!response.body) {
                throw new Error('Response body is not available as a readable stream');
            }
            
            // Create a reader for the stream
            this.reader = response.body.getReader();

            // Process the stream
            const processStream = async (processSrc: string) => {
                let isFirstChunk = true;
                let leftOverBuffer: ArrayBuffer | null = null;
                try {
                    while (processSrc === this.src && this.reader) { // this needs to be fixed.
                        const { done, value } = await this.reader.read();

                        // Proceed only if there is data
                        if (value) {
                            let pcmData: ArrayBuffer;

                            // Process the chunk
                            if (isFirstChunk) {
                                isFirstChunk = false;        
                                // Extract WAV header (first 44 bytes)
                                if (value.length >= 44) {
                                    // Use a copy of the buffer to ensure it's an ArrayBuffer
                                    let wavHeader = value.buffer.slice(0, 44) as ArrayBuffer;
                                    
                                    // Parse WAV header to get sample rate
                                    const dataView = new DataView(wavHeader);

                                    // Sample rate is at offset 24
                                    this.sampleRate = dataView.getUint32(24, true) as unknown as number;

                                    // Extract PCM data (after header)
                                    pcmData = value.buffer.slice(44) as ArrayBuffer;

                                    // Initialize audio context and worklet
                                    await this.initAudioContext(options);

                                    // Ensure that pcm data has a even byte length. Save leftover buffer if odd
                                    if (pcmData.byteLength % 2 !== 0) {
                                        leftOverBuffer = pcmData.slice(pcmData.byteLength - 1);
                                        pcmData = pcmData.slice(0, pcmData.byteLength - 1);
                                    }

                                    //Update duration
                                    this.duration += pcmData.byteLength / (2 * this.sampleRate); // 2 bytes per sample for PCM16

                                    // Send PCM data to worklet. pcmData will be undefined after this call
                                    this.sendPcmDataToWorklet(pcmData);

                                    // Update state to ready
                                    this.updateSnapshot({
                                        isReady: true,
                                        duration: this.duration,
                                    });
                                    
                                    // Autoplay if specified
                                    if (options.autoplay) {
                                        this.play();
                                    }
                                } else {
                                    throw new Error('Invalid WAV header: chunk size too small');
                                }
                            } else {
                                // Process subsequent chunks
                                pcmData = value.buffer as ArrayBuffer;

                                // Check if we have a leftover buffer from the previous chunk and merge it with the current pcmData
                                if (leftOverBuffer) {
                                    const mergedData = new Uint8Array(1 + pcmData.byteLength);
                                    mergedData.set(new Uint8Array(leftOverBuffer), 0);
                                    mergedData.set(new Uint8Array(pcmData), leftOverBuffer.byteLength);
                                    pcmData = mergedData.buffer;
                                    leftOverBuffer = null; // Reset leftover buffer
                                }

                                // Ensure that pcm data has a even byte length. Save leftover buffer if odd
                                if (pcmData.byteLength % 2 !== 0) {
                                    leftOverBuffer = pcmData.slice(pcmData.byteLength - 1);
                                    pcmData = pcmData.slice(0, pcmData.byteLength - 1);
                                }

                                //Update duration
                                this.duration += pcmData.byteLength / (2 * this.sampleRate); // 2 bytes per sample for PCM16                     
                                
                                // Send PCM data to worklet. pcmData will be undefined after this call
                                this.sendPcmDataToWorklet(pcmData);

                                // Update state to ready
                                this.updateSnapshot({
                                    duration: this.duration,
                                });
                            }
                        }

                        // If this is the last chunk, update state
                        // and stop processing
                        if (done) {
                            // this.updateSnapshot({
                            //     isLoading: false,
                            // });

                            // Call onload callback if provided
                            if (options.onload) {
                                options.onload();
                            }

                            // Exit the loop
                            break;
                        }
                    }
                } catch (error) {
                    this.updateSnapshot({
                        error: `Error processing stream: ${error}`
                    });
                }
            };

            // Start processing the stream
            await processStream(options.src);
        } catch (error) {
            this.updateSnapshot({
                isLoading: false,
                isReady: false,
                error: `Failed to load PCM data from URL: ${error}`
            });
        }
    }

    /**
     * Initialize the audio context and worklet
     */
    private async initAudioContext(options: CreateOptions): Promise<void> {
        if (this.audioContext) {
            return;
        }

        try {
            // Create audio context
            this.audioContext = new AudioContext({
                sampleRate: this.sampleRate,
                latencyHint: 'interactive'
            });

            // Load audio worklet
            await this.audioContext.audioWorklet.addModule(StreamProcessorSrc);

            // Create worklet node
            this.workletNode = new AudioWorkletNode(this.audioContext, StreamProcessorName);

            // Create gain node for volume control
            this.gainNode = this.audioContext.createGain();
            this.gainNode.gain.value = this.snapshot.isMuted ? 0 : this.snapshot.volume;

            // Connect nodes
            this.workletNode.connect(this.gainNode);
            this.gainNode.connect(this.audioContext.destination);

            // Set up message handling from worklet
            this.workletNode.port.onmessage = (event) => {
                const { state, currentTime } = event.data;

                if (currentTime !== undefined) {
                    this.currentTime = currentTime;
                    this.position = Math.round(this.currentTime);
                }

                if (state) {
                    switch (state) {
                        case 'playing':
                            this.updateSnapshot({
                                isPlaying: true,
                                isPaused: false,
                                isStopped: false
                            });
                            if (options.onplay) {
                                options.onplay();
                            }
                            break;
                        case 'paused':
                            this.updateSnapshot({
                                isPlaying: false,
                                isPaused: true,
                                isStopped: false
                            });
                            if (options.onpause) {
                                options.onpause();
                            }
                            break;
                        case 'idle':
                            this.updateSnapshot({
                                isPlaying: false,
                                isPaused: false,
                                isStopped: true
                            });
                            if (options.onstop) {
                                options.onstop();
                            }
                            break;
                    }
                }
            };
        } catch (error) {
            this.updateSnapshot({
                error: `Failed to initialize audio context: ${error}`
            });
        }
    }

    /**
     * Send PCM data to the audio worklet
     */
    private sendPcmDataToWorklet(data: ArrayBuffer) {
        if (!this.workletNode) {
            return;
        }

        if (data.byteLength % 2 !== 0) {
            throw new Error('PCM data must have an even byte length (16-bit samples)');
        }

        // Send to worklet
        this.workletNode.port.postMessage({ audioData: data }, [data]);
    }

    /**
     * Load PCM data from a URL
     * The URL should provide audio bytes with a WAV header (first 44 bytes) followed by raw PCM16 bit data
     */
    public async load(options: CreateOptions) {
        // Clean up previous instance if any
        await this.destroy();

        this.initPlayer(options);
    }

    /**
     * Clean up resources
     */
    public async destroy() {
        // 1) abort the fetch
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }

        // 2) cancel the reader
        if (this.reader) {
            try { await this.reader.cancel(); } catch {/*ignore*/}
            this.reader = null;
        }


        // Stop playback if playing
        if (this.snapshot.isPlaying) {
            this.stop();
        }

        // Clean up audio nodes
        if (this.workletNode) {
            this.workletNode.disconnect();
            this.workletNode = null;
        }

        if (this.gainNode) {
            this.gainNode.disconnect();
            this.gainNode = null;
        }

        // Close audio context
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }

        // Reset state
        this.duration = 0;
        this.currentTime = 0;
        this.src = null;
        this.sampleRate = 24000;
        this.updateSnapshot(defaultState);
    }

    /**
     * Subscribe to state changes
     */
    public subscribe(cb: Subscriber) {
        this.subscriptions.add(cb);
        return () => this.subscriptions.delete(cb);
    }

    /**
     * Get current state snapshot
     */
    public getSnapshot() {
        return this.snapshot;
    }

    /**
     * Begin or resume playback
     */
    public async play() {
        if (!this.audioContext || !this.workletNode) {
            return;
        }

        try {
            // Resume audio context if suspended
            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
            }

            if (!this.snapshot.isPlaying) {
                this.workletNode.port.postMessage({ play: true });
            }
        } catch (error) {
            this.updateSnapshot({
                error: `Failed to play PCM audio: ${error}`
            });
        }
    }

    /**
     * Pause playback
     */
    public pause() {
        if (!this.workletNode || !this.snapshot.isPlaying) {
            return;
        }

        try {
            // Send pause command to worklet
            this.workletNode.port.postMessage({ pause: true });
        } catch (error) {
            this.updateSnapshot({
                error: `Failed to pause PCM audio: ${error}`
            });
        }
    }

    /**
     * Toggle between play and pause
     */
    public togglePlayPause() {
        if (this.snapshot.isPlaying) {
            this.pause();
        } else {
            this.play();
        }
    }

    /**
     * Stop playback and reset position
     */
    public stop() {
        if (!this.workletNode) {
            return;
        }

        try {
            // Send clear command to worklet
            this.workletNode.port.postMessage({ clear: true });
            
            // Reset position
            this.currentTime = 0;
            this.position = 0;
            
        } catch (error) {
            this.updateSnapshot({
                error: `Failed to stop PCM audio: ${error}`
            });
        }
    }

    /**
     * Set volume (0-1)
     */
    public setVolume(vol: number) {
        if (this.gainNode) {
            const volume = Math.max(0, Math.min(1, vol));
            this.gainNode.gain.value = this.snapshot.isMuted ? 0 : volume;
             this.updateSnapshot({
                volume
            });
        }
    }

    /**
     * Set playback rate
     */
    public setRate(_: number) {
        // Note: The worklet doesn't support rate changes directly
        // A proper implementation would require resampling the audio data
        throw new Error('Setting playback rate is not supported in this implementation');
    }

    /**
     * Enable looping
     */
    public loopOn() {
        throw new Error('Looping playback is not supported in this implementation');
    }

    /**
     * Disable looping
     */
    public loopOff() {
        throw new Error('Looping playback is not supported in this implementation');
    }

    /**
     * Toggle looping
     */
    public toggleLoop() {
        if (this.snapshot.isLooping) {
            this.loopOff()
        } else {
            this.loopOn()
        }
    }

    /**
     * Mute audio
     */
    public mute() {
        if (this.gainNode) {
            this.gainNode.gain.value = 0;
            this.updateSnapshot({
                isMuted: true
            });
        }
    }

    /**
     * Unmute audio
     */
    public unmute() {
        if (this.gainNode) {
            this.gainNode.gain.value = this.snapshot.volume;
             this.updateSnapshot({
                isMuted: false
            });
        }
    }

    /**
     * Toggle mute
     */
    public toggleMute() {
        if (this.snapshot.isMuted) {
            this.unmute();
        } else {
            this.mute();
        }
    }

    /**
     * Seek to position in seconds
     * Note: This is a basic implementation that doesn't support precise seeking
     */
    public seek(_: number) {
        // Seeking is not fully supported in this implementation
        throw Error('Precise seeking is not fully supported in PCM player');
    }

    /**
     * Get current position in seconds
     */
    public getPosition(): number {
        return this.position;
    }

    /**
     * Fade volume from startVolume to endVolume over durationMs
     */
    public fade(startVolume: number, endVolume: number, durationMs: number) {
        if (!this.gainNode || !this.audioContext) {
            return;
        }
        
        const startVol = Math.max(0, Math.min(1, startVolume));
        const endVol = Math.max(0, Math.min(1, endVolume));
        
        // Set initial volume
        this.gainNode.gain.setValueAtTime(startVol, this.audioContext.currentTime);
        
        // Linear ramp to target volume
        this.gainNode.gain.linearRampToValueAtTime(
            endVol,
            this.audioContext.currentTime + (durationMs / 1000)
        );
        
        // Update state after fade completes
        setTimeout(() => {
            this.updateSnapshot({
                volume: endVol
            });
        }, durationMs);
    }
}
